import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import VerificationBadgesSection from './components/VerificationBadgesSection';
import TrustScoreWidget from './components/TrustScoreWidget';
import ReportManagementPanel from './components/ReportManagementPanel';
import BlockManagementSection from './components/BlockManagementSection';
import CommunityGuidelinesSection from './components/CommunityGuidelinesSection';
import SafetyTipsCarousel from './components/SafetyTipsCarousel';

const SafetyTrustCenter = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading, refreshProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  // Auth check is handled by ProtectedRoute
  // Removed redundant navigation check

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'Shield' },
    { id: 'verification', label: 'Verification', icon: 'CheckCircle' },
    { id: 'reports', label: 'Reports', icon: 'Flag' },
    { id: 'blocked', label: 'Blocked Users', icon: 'Ban' },
    { id: 'guidelines', label: 'Guidelines', icon: 'BookOpen' },
  ];

  return (
    <>
      <Helmet>
        <title>Safety & Trust Center | Anonymous Dating Platform</title>
        <meta name="description" content="Manage your safety settings, verifications, and trust features" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Icon name="Shield" size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Safety & Trust Center</h1>
                <p className="text-muted-foreground">Manage your security, verifications, and community safety</p>
              </div>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => setShowReportModal(true)}
              className="flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-lg hover:bg-error/20 transition-colors"
            >
              <Icon name="AlertTriangle" size={24} className="text-error" />
              <div className="text-left">
                <div className="font-semibold text-foreground">Report a User</div>
                <div className="text-sm text-muted-foreground">File a safety concern immediately</div>
              </div>
            </button>

            <button
              onClick={() => setShowBlockModal(true)}
              className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg hover:bg-warning/20 transition-colors"
            >
              <Icon name="Ban" size={24} className="text-warning" />
              <div className="text-left">
                <div className="font-semibold text-foreground">Block a User</div>
                <div className="text-sm text-muted-foreground">Prevent unwanted interactions</div>
              </div>
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8 border-b border-border">
            <div className="flex gap-2 overflow-x-auto">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeSection === section?.id
                      ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={section?.icon} size={18} />
                  <span>{section?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {activeSection === 'overview' && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <VerificationBadgesSection userProfile={userProfile} onVerificationComplete={refreshProfile} />
                  </div>
                  <div>
                    <TrustScoreWidget userProfile={userProfile} />
                  </div>
                </div>
                <SafetyTipsCarousel />
              </>
            )}

            {activeSection === 'verification' && (
              <VerificationBadgesSection userProfile={userProfile} expanded onVerificationComplete={refreshProfile} />
            )}

            {activeSection === 'reports' && (
              <ReportManagementPanel userId={user?.id} />
            )}

            {activeSection === 'blocked' && (
              <BlockManagementSection userId={user?.id} />
            )}

            {activeSection === 'guidelines' && (
              <CommunityGuidelinesSection />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SafetyTrustCenter;